import { useEffect, useRef, useState } from 'react';
import VideoInstructor from '@/Pages/VideoPlayer/components/VideoInstructor';
import ProductsSeenInVideo from '@/Pages/VideoPlayer/components/ProductsSeenInVideo';
import Comments from '@/Pages/VideoPlayer/comments/Comments';
import useResize from '@/Pages/VideoPlayer/hooks/useResize';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { TRC_TAB_ICONS, TRC_TAB_IDS, TRC_TAB_LABELS } from '@/TRC/constants/trcTabFields';
import GoodTimesFontText from '../goodTimesFontText';
import IsVisibleSection from '../isVisibleSection';
import Header from './components/header';
import About from './components/about';
import Related from './components/related';
import TRC_CONTENT from '@/constants/trcContentType';
import '../../trc.css';

const CommonContent = ({ type, content }) => {
	const device_type = useResize();
	const isMobile = device_type === 'mobile';
	const iconColor = '#008bc3';
	const hasInstructor = !!content?.instructorDetails?.length;
	const hasRelated = !!content?.related?.length;
	const hasEquipments = !!content?.productsData?.length;

	const [open, setOpen] = useState({
		about: true,
		community: true
	});

	useEffect(() => {
		setOpen({
			about: true,
			...(hasInstructor && { instructor: true }),
			...(hasRelated && { related: true }),
			...(hasEquipments && { products: true }),
			community: true
		});
	}, [hasInstructor, hasRelated, hasEquipments]);

	const aboutRef = useRef(null);
	const instructorRef = useRef(null);
	const relatedRef = useRef(null);
	const equipmentRef = useRef(null);
	const chatRef = useRef(null);
	const TRC_TABS = [
		{
			id: TRC_TAB_IDS.ABOUT,
			icon: TRC_TAB_ICONS.ABOUT,
			label: TRC_TAB_LABELS.ABOUT,
			ref: aboutRef,
			show: true
		},
		{
			id: TRC_TAB_IDS.GUEST_INSTRUCTOR,
			icon: TRC_TAB_ICONS.GUEST_INSTRUCTOR,
			label: TRC_TAB_LABELS.GUEST_INSTRUCTOR,
			ref: instructorRef,
			show: hasInstructor
		},
		{
			id: TRC_TAB_IDS.RELATED,
			icon: TRC_TAB_ICONS.RELATED,
			label: TRC_TAB_LABELS.RELATED,
			ref: relatedRef,
			show: hasRelated
		},
		{
			id: TRC_TAB_IDS.EQUIPMENT,
			icon: TRC_TAB_ICONS.EQUIPMENT,
			label: TRC_TAB_LABELS.EQUIPMENT,
			ref: equipmentRef,
			show: hasEquipments
		},
		{ id: TRC_TAB_IDS.CHAT, icon: TRC_TAB_ICONS.CHAT, label: TRC_TAB_LABELS.CHAT, ref: chatRef, show: true }
	].filter(tab => tab.show);

	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (TRC_TABS.length > 0) {
			setActiveIndex(0);
		}
	}, [type]);

	const handleTabClick = idx => {
		setActiveIndex(idx);
		const target = TRC_TABS[idx];
		if (target?.ref?.current) {
			const offsetTop = target.ref.current.getBoundingClientRect().top + window.pageYOffset;
			const headerOffset = 80;
			window.scrollTo({
				behavior: 'smooth',
				top: offsetTop - headerOffset
			});
		}
	};
	const toggleSection = key => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
	return (
		<div className="common-content-container d-flex flex-column">
			<Header tabs={TRC_TABS} activeTab={TRC_TABS[activeIndex]?.id} onTabClick={(_, idx) => handleTabClick(idx)} />

			<div
				className="about-container"
				ref={aboutRef}
				onClick={() =>
					setOpen(prev => ({
						...prev,
						about: !prev.about
					}))
				}
			>
				<div className="d-flex flex-row justify-content-between">
					<GoodTimesFontText
						styles={{
							fontSize: '1.2rem',
							letterSpacing: '0.06rem',
							display: 'flex',
							alignItems: 'center',
							gap: '0.3rem'
						}}
					>
						<TRC_TAB_ICONS.ABOUT />
						{TRC_TAB_LABELS.ABOUT}
					</GoodTimesFontText>
					{isMobile && (open.about ? <RemoveIcon sx={{ color: iconColor }} /> : <AddIcon sx={{ color: iconColor }} />)}
				</div>
				{isMobile ? open.about && <About content={content} /> : <About content={content} />}
			</div>

			{hasInstructor && (
				<div
					ref={instructorRef}
					onClick={() => toggleSection('instructor')}
					className="instructor-container-trc d-flex flex-column"
					
				>
					{isMobile && (
						<div className="d-flex flex-row justify-content-between">
							<GoodTimesFontText styles={{
								fontSize: '1.2rem',
								letterSpacing: '0.06rem',
								display: 'flex',
								alignItems: 'center',
								gap: '0.3rem'
							}}>
								<TRC_TAB_ICONS.GUEST_INSTRUCTOR/>
								{TRC_TAB_LABELS.GUEST_INSTRUCTOR}
							</GoodTimesFontText>
							{open.instructor ? <RemoveIcon sx={{ color: iconColor }} /> : <AddIcon sx={{ color: iconColor }} />}
						</div>
					)}

					<IsVisibleSection isMobile={isMobile} isOpen={open.instructor}>
						<VideoInstructor instructorData={content?.instructorDetails[0]} />
					</IsVisibleSection>
				</div>
			)}

			{hasRelated && (
				<div
					ref={relatedRef}
					onClick={() => toggleSection('related')}
					className="related-container d-flex flex-column"
				>
					<div className="d-flex flex-row justify-content-between">
						<GoodTimesFontText
							styles={{
								fontSize: '1.2rem',
								letterSpacing: '0.06rem',
								display: 'flex',
								alignItems: 'center',
								gap: '0.3rem'
							}}
						>
							<TRC_TAB_ICONS.RELATED />
							{TRC_TAB_LABELS.RELATED}
						</GoodTimesFontText>
						{isMobile &&
						(open.related ? <RemoveIcon sx={{ color: iconColor }} /> : <AddIcon sx={{ color: iconColor }} />)}
					</div>
					
					<IsVisibleSection isMobile={isMobile} isOpen={open.related}>
						<Related content={content} />
					</IsVisibleSection>
				</div>
			)}

			{hasEquipments && (
				<div
					className="product-container"
					
					ref={equipmentRef}
					onClick={() =>
						setOpen(prev => ({
							...prev,
							products: !prev.products
						}))
					}
				>
					{isMobile && (
						<div className="d-flex flex-row justify-content-between">
							<GoodTimesFontText
								styles={{
									fontSize: '1.2rem',
									letterSpacing: '0.06rem',
									display: 'flex',
									alignItems: 'center',
									gap: '0.3rem'
								}}
							>
								<TRC_TAB_ICONS.EQUIPMENT />
								{TRC_TAB_LABELS.EQUIPMENT}
							</GoodTimesFontText>
							{open.products ? <RemoveIcon sx={{ color: iconColor }} /> : <AddIcon sx={{ color: iconColor }} />}
						</div>
					)}
					<IsVisibleSection isMobile={isMobile} isOpen={open.products}>
						<ProductsSeenInVideo
							products={content?.productsData}
							headerText={
								<GoodTimesFontText
									styles={{
										fontSize: '1.2rem',
										letterSpacing: '0.063rem',
										display: 'flex',
										alignItems: 'center',
										gap: '0.3rem'
									}}
								>
									<TRC_TAB_ICONS.EQUIPMENT />
									{TRC_TAB_LABELS.EQUIPMENT}
								</GoodTimesFontText>
							}
							visible={6}
							titleStyles={{
								fontSize: '1.2rem',
								fontWeight: 'normal',
								color: 'white'
							}}
							customStyles={{ width: '100%' }}
							contentType={TRC_CONTENT.TRC}
						/>
					</IsVisibleSection>
				</div>
			)}

			<div
				ref={chatRef}
				className="comment-container d-flex flex-column "
				onClick={() =>
					setOpen(prev => ({
						...prev,
						community: !prev.community
					}))
				}
			>
				{isMobile && (
					<div className="d-flex flex-row justify-content-between">
						<GoodTimesFontText
							styles={{
								fontSize: '1.2rem',
								letterSpacing: '0.06rem',
								display: 'flex',
								alignItems: 'center',
								gap: '0.3rem'
							}}
						>
							<TRC_TAB_ICONS.CHAT />
							{TRC_TAB_LABELS.CHAT}
						</GoodTimesFontText>
						{open.community ? <RemoveIcon sx={{ color: iconColor }} /> : <AddIcon sx={{ color: iconColor }} />}
					</div>
				)}
				<IsVisibleSection isMobile={isMobile} isOpen={open.community}>
					<Comments />
				</IsVisibleSection>
			</div>
		</div>
	);
};

export default CommonContent;
